// ─── Pricing tiers — single source of truth ─────────────────────────────────
//
// Discoverability sprint #15: previously the tier list was hardcoded in BOTH
// `src/pages/Pricing.tsx` and `src/pages/Landing.tsx`, where they had begun to
// drift. This module is the reconciled union of both definitions so each page
// can import it without losing data.
//
// Canonical pricing comes from PRODUCT_VISION_V2.md §4.
//
// Reconciliation notes (Pricing.tsx vs Landing.tsx):
//   • `id` — Pricing typed it as `SubscriptionTier`; Landing used a loose
//     `string`. We keep the typed union (matches `useEntitlement`).
//   • `monthlyPrice` / `annualPrice` — Pricing typed them `number | null`;
//     Landing used `number`. No tier actually carries a null price, so we use
//     `number`.
//   • `perks` — only Landing carried the per-card bullet list. Captured here.
//   • `iconName` — both pages imported a `lucide-react` icon component on the
//     tier object. To keep this module pure data (no component/JSX imports),
//     we store the lucide icon *name* as a string; each page maps it back to
//     the imported component. Names match Pricing/Landing exactly: Sparkles,
//     Zap, Star, Building2.
//   • Prices, taglines, badges, and CTAs were identical across both pages —
//     no value drift to resolve there.
//   • The feature-comparison matrix lived only in Pricing.tsx; it is exported
//     separately below (`PRICING_FEATURES`) so Pricing can rebuild its table.
//
// Pure data + types only — no JSX, no imports from `@/components`.

import type { SubscriptionTier } from '@/hooks/useEntitlement';

/** Names of the `lucide-react` icons used by each tier. Pages map these back to
 *  the imported icon components (keeps this module free of component imports). */
export type PricingTierIconName = 'Sparkles' | 'Zap' | 'Star' | 'Building2';

export interface PricingTier {
  /** Stable tier id; aligns with `SubscriptionTier` from `useEntitlement`. */
  id: SubscriptionTier;
  /** Display name, e.g. "Pro". */
  name: string;
  /** Name of the `lucide-react` icon to render (mapped to a component by the page). */
  iconName: PricingTierIconName;
  /** Price per month in whole USD. `0` for the free tier. */
  monthlyPrice: number;
  /** Price per year in whole USD. `0` for the free tier. */
  annualPrice: number;
  /** Short audience descriptor shown under the price. */
  tagline: string;
  /** Marks the visually emphasized / recommended tier. */
  highlight?: boolean;
  /** Optional ribbon label, e.g. "Most popular". */
  badge?: string;
  /** Call-to-action button label. */
  cta: string;
  /** Per-card bullet highlights (used by Landing's pricing cards). */
  perks: string[];
}

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    iconName: 'Sparkles',
    monthlyPrice: 0,
    annualPrice: 0,
    tagline: 'Casual reseller & side hustle',
    cta: 'Get started free',
    perks: [
      '50 active listings',
      '1 connected platform',
      '25 AI ops / mo',
      'Community comps',
      'All manual features',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    iconName: 'Zap',
    monthlyPrice: 19,
    annualPrice: 190,
    tagline: 'Full-time solo reseller',
    highlight: true,
    badge: 'Most popular',
    cta: 'Start Pro',
    perks: [
      'Unlimited listings',
      'All platforms',
      '1,000 AI ops / mo',
      'Co-Pilot mode',
      'Schedule-C export',
      'Collectibles valuation',
      'MCP read-only',
    ],
  },
  {
    id: 'power',
    name: 'Power',
    iconName: 'Star',
    monthlyPrice: 49,
    annualPrice: 490,
    tagline: 'Shop, VA team & power users',
    cta: 'Start Power',
    perks: [
      'Everything in Pro',
      'Autonomous mode',
      '10,000 AI ops / mo',
      '3 team seats',
      'MCP read + write',
      'API tokens & webhooks',
    ],
  },
  {
    id: 'studio',
    name: 'Studio',
    iconName: 'Building2',
    monthlyPrice: 129,
    annualPrice: 1290,
    tagline: 'Multi-shop, consignment & white-label',
    cta: 'Start Studio',
    perks: [
      'Everything in Power',
      '10 team seats',
      'Consignor portal',
      'Custom branding & SSO',
      'Advanced roles',
      'Onboarding consultant',
    ],
  },
];

// ─── Feature comparison matrix ───────────────────────────────────────────────
//
// Sourced from Pricing.tsx's `FEATURES` table. A `true`/`false` renders as a
// check/cross; a string renders the literal value.

/** A feature-row cell value: `true`/`false` for a check/cross, or literal text. */
export type PricingFeatureValue = boolean | string;

export interface PricingFeature {
  label: string;
  free: PricingFeatureValue;
  pro: PricingFeatureValue;
  power: PricingFeatureValue;
  studio: PricingFeatureValue;
}

export const PRICING_FEATURES: PricingFeature[] = [
  {
    label: 'Active listings',
    free: '50',
    pro: 'Unlimited',
    power: 'Unlimited',
    studio: 'Unlimited',
  },
  { label: 'Connected platforms', free: '1', pro: 'All', power: 'All', studio: 'All' },
  { label: 'AI operations / mo', free: '25', pro: '1,000', power: '10,000', studio: 'Unlimited' },
  { label: 'Team seats', free: '1', pro: '1', power: '3', studio: '10' },
  { label: 'Manual features', free: true, pro: true, power: true, studio: true },
  { label: 'Community comps', free: true, pro: true, power: true, studio: true },
  { label: 'Co-Pilot mode', free: false, pro: true, power: true, studio: true },
  { label: 'Autonomous mode', free: false, pro: false, power: true, studio: true },
  { label: 'Schedule-C export', free: false, pro: true, power: true, studio: true },
  { label: 'Collectibles valuation', free: false, pro: true, power: true, studio: true },
  {
    label: 'MCP server',
    free: false,
    pro: 'Read-only',
    power: 'Read + Write',
    studio: 'Read + Write',
  },
  { label: 'API tokens & webhooks', free: false, pro: false, power: true, studio: true },
  { label: 'Consignor portal', free: false, pro: false, power: false, studio: true },
  { label: 'Custom branding', free: false, pro: false, power: false, studio: true },
  { label: 'SSO', free: false, pro: false, power: false, studio: true },
  {
    label: 'Support',
    free: 'Community',
    pro: 'Email',
    power: 'Phone & chat',
    studio: 'Consultant + SLA',
  },
];
